import { useCallback, useEffect, useState } from "react";
import { MessageCircle, Copy, PencilLine, Send, RefreshCw, Users, Bot } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, StatusPill, DateNav, ItemsEditor, Empty, Money, WaStatus } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { todayIST, fmtDate, fmtDateTime, itemsText, portalUrl, waLink, msgs } from "@/lib/format";

function ManualDialog({ row, date, products, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    setItems(row?.order?.items?.map((i) => ({ product_id: i.product_id, qty: i.qty })) || row?.customer?.default_items || []);
  }, [row]);
  const save = async (skipped) => {
    try {
      await api.post("/orders", { customer_id: row.customer.id, date, items, skipped });
      toast.success(skipped ? "Marked as no batter" : "Order saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="manual-order-dialog">
        <DialogHeader><DialogTitle className="font-display">{row?.customer?.name} · {fmtDate(date)}</DialogTitle></DialogHeader>
        <ItemsEditor products={products} value={items} onChange={setItems} prefix="manual" />
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => save(true)} data-testid="manual-skip-btn">No batter</Button>
          <Button className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={() => save(false)} data-testid="manual-save-btn">Save order</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QueueDialog({ queue, setQueue, onSend }) {
  if (!queue) return null;
  const c = queue.list[queue.i];
  const next = () => (queue.i + 1 >= queue.list.length ? setQueue(null) : setQueue({ ...queue, i: queue.i + 1 }));
  return (
    <Dialog open onOpenChange={(o) => !o && setQueue(null)}>
      <DialogContent className="max-w-md" data-testid="wa-queue-dialog">
        <DialogHeader><DialogTitle className="font-display">WhatsApp queue · {queue.i + 1} of {queue.list.length}</DialogTitle></DialogHeader>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5">
          <div className="font-display text-xl font-bold text-[#0F382C]" data-testid="wa-queue-customer">{c.name}</div>
          <div className="text-sm text-muted-foreground">{c.area} · {c.phone}</div>
        </div>
        <p className="text-sm text-muted-foreground">Tap send. WhatsApp opens with the message ready. Hit send there, then come back for the next customer.</p>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={next} data-testid="wa-queue-skip">Skip</Button>
          <Button className="bg-[#25D366] hover:bg-[#20bd5a] text-white" onClick={async () => { await onSend(c); next(); }} data-testid="wa-queue-send">
            <Send className="mr-2 h-4 w-4" />Send & next
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AutoBanner({ date, onSent }) {
  const [st, setSt] = useState(null);
  useEffect(() => { api.get("/integrations/status").then((r) => setSt(r.data.whatsapp)).catch(() => {}); }, []);
  if (!st) return null;
  const sendNow = async () => {
    try { await api.post("/whatsapp/send-asks", { date }); toast.success("Sending via WhatsApp API to all pending customers..."); setTimeout(onSent, 4000); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!st.configured)
    return (
      <div className="mb-6 flex items-center gap-3 rounded-2xl border border-dashed border-[#D9D2C0] bg-white/70 p-4 text-sm" data-testid="auto-wa-off-banner">
        <Bot className="h-5 w-5 text-muted-foreground" />
        <span>Automatic WhatsApp is <b>not connected yet</b>. Add your WhatsApp Business API keys to auto-send at {st.send_time}. Until then, use the one-tap buttons below.</span>
      </div>
    );
  const lr = st.last_run;
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 text-sm" data-testid="auto-wa-on-banner">
      <div className="flex items-center gap-3">
        <Bot className="h-5 w-5 text-emerald-700" />
        <span>Auto-WhatsApp ({st.provider}) {st.auto_enabled ? <>sends daily at <b>{st.send_time}</b> to customers who haven't replied</> : <b>is paused in Settings</b>}.
          {lr && <span className="text-muted-foreground"> Last run {fmtDateTime(lr.started_at)}: {lr.sent} sent, {lr.failed} failed{lr.error ? ` (${lr.error})` : ""}.</span>}</span>
      </div>
      <Button size="sm" className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={sendNow} data-testid="auto-wa-send-now"><Send className="mr-1 h-4 w-4" />Send now via API</Button>
    </div>
  );
}

export default function Confirmations() {
  const [date, setDate] = useState(todayIST(1));
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [biz, setBiz] = useState("our kitchen");
  const [manual, setManual] = useState(null);
  const [queue, setQueue] = useState(null);

  const load = useCallback(() => api.get("/confirmations", { params: { date } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [date]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/settings").then((r) => setBiz(r.data.business_name));
  }, []);

  const send = async (c) => {
    window.open(waLink(c.phone, msgs.order(c, date, biz)), "_blank");
    await api.post("/confirmations/mark-sent", { date, customer_ids: [c.id] }).catch(() => {});
    toast.success(`WhatsApp opened for ${c.name}`);
    load();
  };
  const copy = (c) => { navigator.clipboard?.writeText(portalUrl(c.token, "order")); toast.success("Order link copied"); };
  const pending = data?.rows.filter((r) => r.status === "pending").map((r) => r.customer) || [];

  return (
    <div data-testid="confirmations-page">
      <PageHeader eyebrow="Sales confirmation" title="Tomorrow's WhatsApp orders"
        subtitle="Send each customer a personal link. Their answer comes straight in here and drives the production plan. The page refreshes every 20 seconds.">
        <DateNav value={date} onChange={setDate} />
        <Button variant="outline" onClick={load} data-testid="refresh-confirmations-btn"><RefreshCw className="h-4 w-4" /></Button>
        <Button disabled={!pending.length} onClick={() => setQueue({ list: pending, i: 0 })} className="bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-full" data-testid="start-wa-queue-btn">
          <MessageCircle className="mr-2 h-4 w-4" />Ask all pending ({pending.length})
        </Button>
      </PageHeader>
      <AutoBanner date={date} onSent={load} />

      {data && (
        <div className="grid gap-4 md:grid-cols-4 mb-8">
          {["confirmed", "pending", "skipped"].map((s) => (
            <div key={s} className="rounded-2xl border border-[#E7E2D6] bg-white p-5" data-testid={`count-${s}`}>
              <StatusPill status={s} label={s === "skipped" ? "No need" : s} />
              <div className="font-mono text-3xl font-semibold mt-3">{data.counts[s]}</div>
            </div>
          ))}
          <div className="rounded-2xl bg-[#0F382C] p-5 text-white" data-testid="product-totals">
            <div className="text-xs uppercase tracking-wider text-emerald-200">To produce</div>
            <div className="mt-2 space-y-1 text-sm">
              {data.product_totals.length === 0 && <span className="text-emerald-100/70">Waiting for confirmations</span>}
              {data.product_totals.map((p) => <div key={p.product_id} className="flex justify-between"><span>{p.name}</span><span className="font-mono font-semibold">{p.qty}</span></div>)}
            </div>
          </div>
        </div>
      )}

      {data && data.rows.length === 0 && <Empty icon={Users} title="No active customers" text="Add customers first to start sending order confirmations." testid="confirmations-empty" />}
      <div className="space-y-3">
        {data?.rows.map((r) => (
          <div key={r.customer.id} className="flex flex-col gap-3 rounded-2xl border border-[#E7E2D6] bg-white p-4 md:flex-row md:items-center md:justify-between hover:shadow-md transition-shadow" data-testid={`confirmation-row-${r.customer.id}`}>
            <div className="min-w-0 md:w-1/4">
              <div className="font-semibold">{r.customer.name}</div>
              <div className="text-xs text-muted-foreground">{r.customer.area} · {r.customer.phone}</div>
            </div>
            <div className="md:w-28"><StatusPill status={r.status} label={r.status === "skipped" ? "No need" : r.status === "paused" ? "On holiday" : r.order?.source === "standing" ? "Standing" : r.status} testid={`status-${r.customer.id}`} /></div>
            <div className="flex-1 text-sm">
              {r.order?.status === "confirmed" ? <span>{itemsText(r.order.items)} · <Money value={r.order.total} className="font-semibold" />{r.order.slot && <span className="ml-1 text-xs text-muted-foreground" data-testid={`slot-${r.customer.id}`}>· {r.order.slot}</span>}</span> : <span className="text-muted-foreground">{r.asked_at ? `Asked ${fmtDateTime(r.asked_at)}` : "Not asked yet"}</span>}
              {r.wa && <span className="ml-2"><WaStatus msg={r.wa} testid={`wa-status-${r.customer.id}`} /></span>}
              {r.order?.source === "whatsapp_link" && <span className="ml-2 text-xs text-emerald-700">via WhatsApp link</span>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-[#25D366] hover:bg-[#20bd5a] text-white" onClick={() => send(r.customer)} data-testid={`wa-send-${r.customer.id}`}><MessageCircle className="mr-1 h-4 w-4" />WhatsApp</Button>
              <Button size="sm" variant="outline" onClick={() => copy(r.customer)} data-testid={`copy-link-${r.customer.id}`}><Copy className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => setManual(r)} data-testid={`manual-${r.customer.id}`}><PencilLine className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <ManualDialog row={manual} date={date} products={products} onClose={() => setManual(null)} onSaved={load} />
      <QueueDialog queue={queue} setQueue={setQueue} onSend={send} />
    </div>
  );
}
